import React, { useState } from 'react';
import api from '../../services/api';
import { showToast } from '../layout/ToastContainer';

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function ResultDetail({ record, onClose, onDelete, onUpdate }) {
  const [rec, setRec] = useState(record);
  const [togglingDone, setTogglingDone] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailMsg, setEmailMsg] = useState(() => {
    const name = record?.user?.name || '';
    const first = name.split(' ')[0] || name;
    return `Hi ${first},\n\n`;
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  if (!rec) return null;
  const { user, speedtest, client, createdAt, id } = rec;
  const firstName = (user.name || '').split(' ')[0] || user.name;

  function InfoRow({ label, value, mono = false }) {
    return (
      <div style={{ display: 'flex', padding: '9px 0', borderBottom: '1px solid var(--border-dim)' }}>
        <div style={{ width: 180, color: 'var(--text-muted)', fontSize: 13, flexShrink: 0 }}>{label}</div>
        <div style={{
          color: 'var(--text-primary)', fontSize: 13,
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          wordBreak: 'break-all', flex: 1
        }}>{value || '—'}</div>
      </div>
    );
  }

  async function toggleDone() {
    setTogglingDone(true);
    try {
      const { data } = await api.patch(`/results/${id}`, { done: !rec.done });
      setRec(data);
      onUpdate && onUpdate(data);
      showToast(data.done ? 'Marked as done' : 'Marked as pending', 'success');
    } catch {
      showToast('Failed to update status', 'error');
    } finally {
      setTogglingDone(false);
    }
  }

  async function sendEmail() {
    if (!emailMsg.trim()) return;
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/results/${id}/email`, { message: emailMsg.trim() });
      const updated = { ...rec, emailLog: data.emailLog };
      setRec(updated);
      onUpdate && onUpdate(updated);
      setEmailMsg(`Hi ${firstName},\n\n`);
      setEmailModal(false);
      showToast('Email sent successfully', 'success');
    } catch {
      showToast('Failed to send email', 'error');
    } finally {
      setSendingEmail(false);
    }
  }

  function downloadImage(filename) {
    const token = localStorage.getItem('speedtest_token');
    fetch(`/api/results/${id}/images/${encodeURIComponent(filename)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => showToast('Failed to download image', 'error'));
  }

  function downloadPDF() {
    const token = localStorage.getItem('speedtest_token');
    fetch(`/api/results/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => showToast('Failed to download PDF', 'error'));
  }

  const emailLog = rec.emailLog || [];
  const images = rec.images || [];

  // Word count helper
  const wordCount = emailMsg.trim() ? emailMsg.trim().split(/\s+/).length : 0;

  return (
    <>
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: 720 }}>
          <div className="modal-header">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              Speed Test Report
              {rec.done && (
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                  color: '#00c88c', background: 'rgba(0,200,140,0.12)',
                  padding: '3px 9px', borderRadius: 10
                }}>DONE</span>
              )}
            </h2>
            <button className="btn btn-ghost btn-icon" onClick={onClose}
              style={{ fontSize: 18, padding: '4px 10px' }}>✕</button>
          </div>
          <div className="modal-body">
            {/* Date + ID */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(createdAt)}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>{id}</div>
            </div>

            {/* Speed metrics */}
            <div className="grid-4" style={{ marginBottom: 24 }}>
              {[
                { label: 'Download', value: speedtest.download.toFixed(1), unit: 'Mbps' },
                { label: 'Upload',   value: speedtest.upload.toFixed(1),   unit: 'Mbps' },
                { label: 'Ping',     value: speedtest.ping.toFixed(0),     unit: 'ms'   },
                { label: 'Jitter',   value: speedtest.jitter.toFixed(1),   unit: 'ms'   },
              ].map(m => (
                <div key={m.label} className="card" style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--accent-orange)' }}>
                    {m.value}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{m.unit}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{m.label}</div>
                </div>
              ))}
            </div>

            {/* User info */}
            <div style={{ marginBottom: 20 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>User Information</div>
              <InfoRow label="Name" value={user.name} />
              <InfoRow label="Email" value={user.email} mono />
              <InfoRow label="Phone" value={user.phone} />
            </div>

            {/* Issue description */}
            <div style={{ marginBottom: 20 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>Issue Description</div>
              <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                borderRadius: 'var(--radius-md)', padding: 14,
                fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: 'var(--text-primary)'
              }}>{user.description}</div>
            </div>

            {/* Feature requests */}
            <div style={{ marginBottom: 20 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>Feature Requests</div>
              <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                borderRadius: 'var(--radius-md)', padding: 14,
                fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: 'var(--text-primary)'
              }}>{user.features || <span style={{ color: 'var(--text-muted)' }}>None provided</span>}</div>
            </div>

            {/* Uploaded Screenshots */}
            {images.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div className="section-title" style={{ marginBottom: 8 }}>
                  Uploaded Screenshots ({images.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {images.map((filename, i) => (
                    <button key={i} onClick={() => downloadImage(filename)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                        borderRadius: 'var(--radius-sm)', padding: '7px 12px',
                        color: 'var(--accent-orange)', fontSize: 12, cursor: 'pointer',
                        fontFamily: 'var(--font-mono)', textAlign: 'left'
                      }}>
                      &#128247; {filename}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Client info */}
            <div style={{ marginBottom: emailLog.length > 0 ? 20 : 0 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>Connection & Device Info</div>
              <InfoRow label="IP Address" value={client.ip} mono />
              <InfoRow label="ISP" value={client.isp} />
              <InfoRow label="Operating System" value={client.os} />
              <InfoRow label="Browser" value={client.browser} />
              <InfoRow label="Viewport" value={`${client.viewport.width} × ${client.viewport.height}px`} mono />
              <InfoRow label="Screen Resolution" value={`${client.screenResolution.width} × ${client.screenResolution.height}px`} mono />
              <InfoRow label="Device Pixel Ratio" value={`${client.devicePixelRatio}x`} />
              <InfoRow label="Color Depth" value={`${client.colorDepth}-bit`} />
              <InfoRow label="Language" value={client.language} />
              <InfoRow label="Timezone" value={client.timezone} />

              <InfoRow label="Cookies" value={client.cookiesEnabled ? 'Enabled' : 'Disabled'} />
              <div style={{ marginTop: 12 }}>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>User Agent</div>
                <div style={{
                  background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                  borderRadius: 'var(--radius-sm)', padding: '8px 12px',
                  fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)',
                  wordBreak: 'break-all', lineHeight: 1.5
                }}>{client.userAgent}</div>
              </div>
            </div>

            {/* Email Log */}
            {emailLog.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div className="section-title" style={{ marginBottom: 8 }}>
                  Email History ({emailLog.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {emailLog.map((entry, i) => (
                    <div key={i} style={{
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                      borderRadius: 'var(--radius-sm)', padding: 12
                    }}>
                      <div style={{ fontSize: 11, color: '#7db8f7', marginBottom: 6, fontWeight: 600 }}>
                        ✉ Sent {formatDateShort(entry.sentAt)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {entry.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(id)}>Delete</button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={toggleDone}
              disabled={togglingDone}
              style={{
                color: rec.done ? '#00c88c' : '#9aa3b0',
                borderColor: rec.done ? 'rgba(0,200,140,0.4)' : '#3e4a5a'
              }}>
              {togglingDone ? '...' : rec.done ? '✓ Done' : 'Mark Done'}
            </button>
            <button className="btn btn-ghost btn-sm"
              onClick={() => { setEmailMsg(`Hi ${firstName},\n\n`); setEmailModal(true); }}
              style={{ color: '#7db8f7', borderColor: 'rgba(100,160,255,0.35)' }}>
              Email User
            </button>
            <button className="btn btn-ghost btn-sm" onClick={downloadPDF}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              Download PDF
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {emailModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}
          onClick={e => e.target === e.currentTarget && setEmailModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Email {user.name}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setEmailModal(false)}
                style={{ fontSize: 18, padding: '4px 10px' }}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                Sending to: <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>{user.email}</span>
              </div>
              <textarea
                className="input-field"
                rows={10}
                placeholder="Type your message here..."
                value={emailMsg}
                onChange={e => setEmailMsg(e.target.value)}
                style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              />
              <div style={{ fontSize: 12, color: wordCount > 500 ? 'var(--accent-red)' : 'var(--text-muted)', marginTop: 6, textAlign: 'right' }}>
                {wordCount} / 500 words
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => { setEmailModal(false); setEmailMsg(`Hi ${firstName},\n\n`); }}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm"
                onClick={sendEmail}
                disabled={sendingEmail || !emailMsg.trim() || wordCount > 500}>
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
